import React from 'react';
import {
  MIN_ZOOM_FACTOR,
  type Point,
  toPoint,
} from '@packages/systems/open-canvas/core';
import {
  type ContinuousViewportChangeActionType,
  type CanvasViewportEventTrigger,
} from '@packages/systems/open-canvas/store';
import {usePanDragLifecycle} from './usePanDragLifecycle';

type ViewportEventHandlersProps = {
  contentWindow?: Window | null;
  onCanvasViewportPanned?: (dx: number, dy: number) => void;
  onCanvasViewportZoomed?: (zoomFactor: number, originPoint: Point) => void;
  onCanvasViewportChangeStarted?: (
    actionType: ContinuousViewportChangeActionType,
    trigger: CanvasViewportEventTrigger
  ) => void;
  onCanvasViewportChangeEnded?: (
    actionType: ContinuousViewportChangeActionType,
    trigger: CanvasViewportEventTrigger
  ) => void;
};

/**
 * Adjust the sensitivity of zoom interactions
 */
export const ZOOM_SENSITIVITY = 0.4;

/**
 * Given the `deltaY` amount obtained from a `wheel` event, computes the `zoomFactor` value to use
 * when updating the zoom state.
 *
 * Different devices may produce different `deltaY` values. This function normalizes the
 * raw `deltaY` to account for differences between different devices like a mouse or trackpad.
 *
 * Larger values of `deltaY` that can be produced by aggressive scrolling for example are dampened.
 */
const computeZoomFactor = (rawDeltaY: number): number => {
  // Normalize input range to lower values
  const normalizedDelta = rawDeltaY / 100;

  // Direction of the deltaY change, (-1) or (1)
  const sign = Math.sign(normalizedDelta);

  // Get the amount of deltaY change
  const magnitude = Math.abs(normalizedDelta);

  // Adjust the magnitude to a [0, 1] range, such that large values
  // are adjusted to 1, and small values are amplified by sqrt.
  const adjustedMagnitude = Math.min(1, Math.sqrt(magnitude));

  const adjustedDelta = sign * adjustedMagnitude;

  const computedZoomFactor = 1 - adjustedDelta * ZOOM_SENSITIVITY;
  return Math.max(computedZoomFactor, MIN_ZOOM_FACTOR);
};

/**
 * For Viewport pan/zoom interactions, we create separate event handlers because we need to attach them higher up in the DOM.
 * This is different to `onWheel` and `bodyEventHandlers`, which are attached from the Designer's `Canvas` to the rendered `body` element in `Body.tsx`.
 *
 * The _reason_ it needs to be higher up is because scale & pan transformations are applied to the `body` element, which would otherwise
 * result in a blank space that cannot be interacted with.
 */
export const ViewportEventHandlers = (props: ViewportEventHandlersProps) => {
  const {
    contentWindow,
    onCanvasViewportPanned,
    onCanvasViewportZoomed,
    onCanvasViewportChangeStarted,
    onCanvasViewportChangeEnded,
  } = props;

  const resetWheelEventTimers = useWheelEventTimers({
    onCanvasViewportChangeStarted,
    onCanvasViewportChangeEnded,
  });

  React.useEffect(() => {
    const onViewportWheel = (event: WheelEvent) => {
      const isZoomEvent = event.ctrlKey || event.metaKey;
      const isPanEvent = !isZoomEvent;

      if (isPanEvent || isZoomEvent) {
        // Reset interaction timers if needed
        resetWheelEventTimers(event);
        // Take over wheel event below
        event.stopPropagation();

        // Prevent default browser wheel behaviour
        event.preventDefault();
      }

      if (isPanEvent) {
        // Pan horizontally if Shift is pressed
        if (event.shiftKey) {
          const deltaX = event.deltaY || event.deltaX;
          onCanvasViewportPanned?.(deltaX, 0);
        } else {
          onCanvasViewportPanned?.(event.deltaX, event.deltaY);
        }
      } else if (isZoomEvent) {
        const zoomFactor = computeZoomFactor(event.deltaY);
        const originPoint = toPoint(event.clientX, event.clientY);

        onCanvasViewportZoomed?.(zoomFactor, originPoint);
      }
    };

    const onContentWindowScroll = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    // Fixes tab scroll issue see: https://webflow.atlassian.net/browse/DEX-594
    const onContentWindowFocusIn = () => {
      const doc = contentWindow?.document;
      if (!doc) return;

      if (doc.documentElement) {
        // Intentional DOM mutation in an event listener that the React compiler doesn't control
        // eslint-disable-next-line react-hooks/react-compiler
        doc.documentElement.scrollTop = 0;
      }
    };

    if (contentWindow) {
      const doc = contentWindow.document;

      contentWindow.addEventListener('wheel', onViewportWheel, {
        passive: false,
      });
      contentWindow.addEventListener('scroll', onContentWindowScroll);
      doc.addEventListener('focusin', onContentWindowFocusIn);

      return () => {
        contentWindow.removeEventListener('wheel', onViewportWheel);
        contentWindow.removeEventListener('scroll', onContentWindowScroll);
        doc.removeEventListener('focusin', onContentWindowFocusIn);
      };
    }
  }, [
    contentWindow,
    onCanvasViewportPanned,
    onCanvasViewportZoomed,
    resetWheelEventTimers,
  ]);

  React.useEffect(() => {
    if (contentWindow) {
      contentWindow.scrollTo(0, 0);
    }
  }, [contentWindow]);

  useMiddleMousePan({
    contentWindow,
    onCanvasViewportPanned,
    onCanvasViewportChangeStarted,
    onCanvasViewportChangeEnded,
  });

  return null;
};

/**
 * The amount of time to wait before considering a wheel event to be complete
 */
export const WHEEL_EVENT_TIMEOUT_MS = 500;

type UseWheelEventTimers = {
  onCanvasViewportChangeStarted?: (
    actionType: ContinuousViewportChangeActionType,
    trigger: 'wheel-event'
  ) => void;
  onCanvasViewportChangeEnded?: (
    actionType: ContinuousViewportChangeActionType,
    trigger: 'wheel-event'
  ) => void;
};

const MIDDLE_BUTTON = 1;

/**
 * Bitmask used to test `MouseEvent.buttons` for the middle mouse button.
 * (Note this differs from `MouseEvent.button`, which is `1` for middle.)
 */
const MIDDLE_BUTTONS_FLAG = 4;

type UseMiddleMousePanProps = {
  contentWindow?: Window | null;
  onCanvasViewportPanned?: (dx: number, dy: number) => void;
  onCanvasViewportChangeStarted?: (
    actionType: ContinuousViewportChangeActionType,
    trigger: 'middle-mouse-pan-event'
  ) => void;
  onCanvasViewportChangeEnded?: (
    actionType: ContinuousViewportChangeActionType,
    trigger: 'middle-mouse-pan-event'
  ) => void;
};

/**
 * Handles iframe-initiated middle-mouse drags. The originating `mousedown`
 * happens inside the iframe document, which gets implicit pointer capture
 * for that pointer -- parent-frame handlers (including `PanSurface`) never
 * receive its pointer events. So we track the full lifecycle here.
 *
 * For drags initiated *outside* the iframe (e.g. middle-click on a frame
 * toolbar), the parent-frame `PanSurface` observes the moves directly --
 * `PanSurfaceContainer` applies `pointer-events: none` to this iframe so
 * events that would otherwise hit-test inside it atravessam pra
 * `PanSurface`. This hook contributes nothing in that case.
 */
const useMiddleMousePan = ({
  contentWindow,
  onCanvasViewportPanned,
  onCanvasViewportChangeStarted,
  onCanvasViewportChangeEnded,
}: UseMiddleMousePanProps) => {
  /**
   * Set to true on `mouseleave` from the iframe while a drag is active. The
   * next `mousemove` (cursor returned) consumes the flag and checks
   * `MouseEvent.buttons` to detect the "released outside the browser" case.
   * We only check `buttons` on this single transitional event so that normal
   * in-iframe drags (which never trigger `mouseleave`) are not affected by
   * any environment where `buttons` is unreliable mid-drag.
   */
  const cursorLeftIframeRef = React.useRef(false);

  /**
   * Reference to the cursor style element injected into the iframe document
   */
  const cursorStyleRef = React.useRef<HTMLStyleElement | null>(null);

  const handleDragEnded = React.useCallback(() => {
    onCanvasViewportChangeEnded?.('pan', 'middle-mouse-pan-event');
  }, [onCanvasViewportChangeEnded]);

  const lifecycle = usePanDragLifecycle({
    onPanned: onCanvasViewportPanned,
    onPanEnded: handleDragEnded,
  });

  const {beginDrag, applyMove, endDrag, isDragging} = lifecycle;

  React.useEffect(() => {
    if (!contentWindow) return;

    const iframeDoc = contentWindow.document;
    if (!iframeDoc) return;

    const createCursorStyle = () => {
      const cursorStyle = iframeDoc.createElement('style');
      cursorStyle.dataset.middleMousePan = 'true';
      cursorStyle.textContent = '* { cursor: grabbing !important; }';
      iframeDoc.head.appendChild(cursorStyle);

      cursorStyleRef.current = cursorStyle;
    };

    const removeCursorStyle = () => {
      if (cursorStyleRef.current) {
        cursorStyleRef.current.remove();
        cursorStyleRef.current = null;
      }
    };

    /**
     * Iframe-initiated drags need cleanup in three failure modes where we
     * wouldn't otherwise see the release:
     *
     * - Iframe loses focus / tab hidden -> caught by `blur` on
     *   `contentWindow` and `visibilitychange` on `iframeDoc`.
     * - Release outside the browser entirely (OS taskbar, dock, alt-tab to
     *   another app) -> nothing fires anywhere in the page while the cursor
     *   is gone. The only signal is when the cursor returns to the iframe:
     *   `mouseleave` set a flag, and the very next `mousemove` checks
     *   `MouseEvent.buttons`. We deliberately limit the `buttons` check to
     *   this single transitional event because some mouse setups don't
     *   reliably preserve the middle bit on every mousemove during a drag.
     */
    const finishDrag = () => {
      const wasActive = endDrag();
      if (!wasActive) return;

      removeCursorStyle();
      cursorLeftIframeRef.current = false;
    };

    const handleVisibilityChange = () => {
      if (iframeDoc.visibilityState === 'hidden') {
        finishDrag();
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button !== MIDDLE_BUTTON) return;
      e.preventDefault();

      beginDrag(e.clientX, e.clientY);
      createCursorStyle();
      cursorLeftIframeRef.current = false;

      onCanvasViewportChangeStarted?.('pan', 'middle-mouse-pan-event');
    };

    const handleIframeMouseLeave = () => {
      if (isDragging()) {
        cursorLeftIframeRef.current = true;
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging()) return;

      // Recovery for "released outside the browser": if the cursor left
      // the iframe and is now back, we couldn't observe a release event
      // but the user may have let go while outside. Check `buttons` only
      // on this single transitional move -- if middle is no longer held,
      // end the drag instead of treating this stray move as a pan.
      if (cursorLeftIframeRef.current) {
        cursorLeftIframeRef.current = false;
        if ((e.buttons & MIDDLE_BUTTONS_FLAG) === 0) {
          finishDrag();
          return;
        }
      }

      applyMove(e.clientX, e.clientY);
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (e.button !== MIDDLE_BUTTON) return;
      finishDrag();
    };

    const handleAuxClick = (e: MouseEvent) => {
      if (e.button === MIDDLE_BUTTON) {
        e.preventDefault();
      }
    };

    iframeDoc.addEventListener('mousedown', handleMouseDown, true);
    iframeDoc.addEventListener('mousemove', handleMouseMove);
    iframeDoc.addEventListener('mouseup', handleMouseUp, true);
    iframeDoc.addEventListener('auxclick', handleAuxClick, true);
    iframeDoc.addEventListener('mouseleave', handleIframeMouseLeave);
    iframeDoc.addEventListener('visibilitychange', handleVisibilityChange);
    contentWindow.addEventListener('blur', finishDrag);

    return () => {
      iframeDoc.removeEventListener('mousedown', handleMouseDown, true);
      iframeDoc.removeEventListener('mousemove', handleMouseMove);
      iframeDoc.removeEventListener('mouseup', handleMouseUp, true);
      iframeDoc.removeEventListener('auxclick', handleAuxClick, true);
      iframeDoc.removeEventListener('mouseleave', handleIframeMouseLeave);
      iframeDoc.removeEventListener('visibilitychange', handleVisibilityChange);
      contentWindow.removeEventListener('blur', finishDrag);

      finishDrag();
    };
  }, [
    applyMove,
    beginDrag,
    contentWindow,
    endDrag,
    isDragging,
    onCanvasViewportChangeStarted,
  ]);
};

const useWheelEventTimers = ({
  onCanvasViewportChangeStarted,
  onCanvasViewportChangeEnded,
}: UseWheelEventTimers) => {
  const viewportWheelTimeout = React.useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const isWheelEventInProgress = React.useRef<boolean>(false);

  const resetWheelEventTimers = React.useCallback(
    (e: WheelEvent) => {
      const actionType: ContinuousViewportChangeActionType =
        e.ctrlKey || e.metaKey ? 'zoom' : 'pan';

      // Clear existing timeout if one is active
      if (isWheelEventInProgress.current) {
        clearTimeout(viewportWheelTimeout.current);
      }

      // Start the wheel event if not already in progress
      if (!isWheelEventInProgress.current) {
        isWheelEventInProgress.current = true;
        onCanvasViewportChangeStarted?.(actionType, 'wheel-event');
      }

      // Reset timeout to end the wheel event
      viewportWheelTimeout.current = setTimeout(() => {
        onCanvasViewportChangeEnded?.(actionType, 'wheel-event');
        isWheelEventInProgress.current = false;
      }, WHEEL_EVENT_TIMEOUT_MS);
    },
    [onCanvasViewportChangeEnded, onCanvasViewportChangeStarted]
  );

  return resetWheelEventTimers;
};
